/*
 * True Relevance Design System V3 — tokens, transcribed from the design system's own
 * tokens/colors.css, tokens/spacing.css and tokens/typography.css.
 *
 * Keep this file a straight transcription. If a value needs to change, change it in the design
 * system first and mirror it here, so the portal never becomes the source of truth for the brand.
 */

/** The four brand hues. NEVER painted solid — see `alpha()` below. */
export const brand = {
  pink: '#F5619F', // Empathy — emotion
  yellow: '#F1D751', // Impulse — drive
  blue: '#4EC2DF', // Clarity — cognition
  lime: '#9DCF57', // Human — people
} as const;

/** Higher-contrast Impulse Yellow, for a solid icon circle only. */
export const yellowDeep = '#D6D028';

export const neutral = {
  ink: '#2F2F2F', // text, logo, icons — the brand "black"
  inkTrue: '#000000', // display headlines only
  white: '#FFFFFF',
  offWhite: '#F8F7F8', // secondary surface
  line: '#E6E6E6', // hairline borders
  mute: '#707070', // muted / secondary text
  muteSoft: '#B5B5B5', // tertiary / disabled text
} as const;

/** Functional status colours. The design system marks these "functional UI only, kept quiet". */
export const status = {
  error: '#D05B5B',
  success: '#5BA86B',
} as const;

const RGB: Record<keyof typeof brand, string> = {
  pink: '245, 97, 159',
  yellow: '241, 215, 81',
  blue: '78, 194, 223',
  lime: '157, 207, 87',
};

/**
 * The working form of every brand colour: a transparent fill at 20–50%.
 *
 * This is the design system's core rule — the brand hues appear only as transparent layers that
 * mix where they overlap. It is also a hard accessibility fact here: measured against white, the
 * four hues land at 1.44:1 (yellow) to 2.96:1 (pink), so none of them can carry text or act as a
 * solid UI surface. Text and controls use ink; the hues tint.
 */
export function alpha(hue: keyof typeof brand, pct: 20 | 30 | 40 | 50): string {
  return `rgba(${RGB[hue]}, ${(pct / 100).toFixed(2)})`;
}

/**
 * Interactive blue, derived from Clarity Blue by dropping lightness while holding the hue.
 *
 * Clarity Blue itself is 2.08:1 on white — unusable for links or focused controls. This derivation
 * is the design system's stated escape hatch ("if too restrictive, define harmonious colors
 * matching the existing palette; don't invent new colors from scratch") and measures 6.0:1 on
 * white, comfortably past WCAG AA.
 */
export const blueDeep = '#186C81';
/** Hover/active partner for `blueDeep`, one step darker again (7.6:1 on white). */
export const blueDeeper = '#125C6E';

/** Radii. The shape vocabulary is circles and pills, so nothing is ever square-cornered. */
export const radius = {
  sm: 8,
  md: 14, // soft cards
  lg: 24,
  xl: 40,
  pill: 999,
} as const;

/** Shadows — soft, low, diffuse, grey. Never harsh. */
export const shadow = {
  xs: '0 1px 2px rgba(47, 47, 47, 0.06)',
  sm: '0 2px 8px rgba(47, 47, 47, 0.06)',
  md: '0 8px 24px rgba(47, 47, 47, 0.08)',
  lg: '0 18px 48px rgba(47, 47, 47, 0.10)',
} as const;

/** Motion — gentle. No bounce, no spin, no infinite loops. */
export const motion = {
  easeSoft: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  fast: '140ms',
  base: '240ms',
  slow: '420ms',
} as const;

/**
 * Gabarito is the brand face; Nunito Sans is the official Office/web fallback, chosen because it
 * shares Gabarito's geometry. Both are loaded from Google Fonts in packages/app/public/index.html.
 *
 * Note: Gabarito ships no true Light cut, so the brand's "Gabarito Light" body style is Regular
 * (400) — that is the design system's own resolution, not an approximation made here.
 */
export const fontFamily = [
  '"Gabarito"',
  '"Nunito Sans"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ');

export const weight = {
  light: 400, // no true Light cut exists
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/*
 * NOTE: the grid-bound circle/pill motif that carries this brand lives in
 * packages/app/src/brand.css as `--tr-header-shapes`, with the cell maths documented there.
 * It is CSS rather than TypeScript because the header it decorates is a @backstage/ui component
 * styled by plain CSS classes, not a MUI component.
 */
